import type { CumulativeScopeChapterView } from '@/api/lcLearning'
import { courseDisplayName } from '@/utils/courseName'
import type { CourseNameLabels } from '@/utils/courseName'

export interface CumulativeScopeGroup {
  /** 사람이 읽는 과정 이름. 콘텐츠에 과정이 연결되지 않은 챕터는 null. */
  courseName: string | null
  chapters: { ordinal: number; title: string | null }[]
}

/**
 * 시험범위를 누적 리포트와 같은 모양(`코어 C · 챕터 11. …`)으로 묶는다. 과정은 연속된 챕터가 같은 과정일 때만
 * 하나로 합쳐, 범위가 과정 경계를 넘어가면 바뀐 지점에서 과정 이름을 다시 보여준다.
 */
export function groupScopeChaptersByCourse(
  chapters: CumulativeScopeChapterView[] | undefined,
  labels: CourseNameLabels,
): CumulativeScopeGroup[] {
  const groups: CumulativeScopeGroup[] = []
  ;(chapters ?? []).forEach((chapter) => {
    const courseName = chapter.courseTitle ? courseDisplayName(chapter.courseTitle, labels) : null
    // 표기는 과정 안에서 1부터 센 번호다 — 전역 번호를 쓰면 어드밴스드 첫 챕터가 "챕터 19"가 된다.
    const entry = { ordinal: chapter.chapterNumberInCourse ?? chapter.chapterOrdinal, title: chapter.chapterTitle }
    const last = groups.at(-1)
    if (last && last.courseName === courseName) last.chapters.push(entry)
    else groups.push({ courseName, chapters: [entry] })
  })
  return groups
}

export interface CumulativeScopeRange {
  courseName: string | null
  start: number
  end: number
}

/**
 * 시험범위를 과정별 연속 구간으로 접는다(`코어 A1 1~3`). 챕터 제목이 필요 없는 LMS 카드용이며,
 * 번호는 [groupScopeChaptersByCourse] 와 같은 과정 내 번호를 쓴다.
 */
export function scopeRangesByCourse(
  chapters: CumulativeScopeChapterView[] | undefined,
  labels: CourseNameLabels,
): CumulativeScopeRange[] {
  return groupScopeChaptersByCourse(chapters, labels).map((group) => ({
    courseName: group.courseName,
    start: group.chapters[0].ordinal,
    end: group.chapters[group.chapters.length - 1].ordinal,
  }))
}
